/**
 * Flat comments -> reply threads.
 *
 * Comments arrive flat; a reply names its parent by the platform comment id
 * (`parentCommentId` -> the parent's `commentId`). Top-level comments come
 * first, newest first; replies sit under their parent, oldest first, so a
 * conversation reads top to bottom.
 *
 * A reply whose parent is not in `visible` (filtered out by a sentiment tab,
 * or past the 500-comment cap) is not lost: it becomes a top-level entry, and
 * `replyTo` carries the parent from `all` when it is known, so the UI can say
 * who it answered.
 */
export interface CommentThread<T> {
  item: T
  replies: CommentThread<T>[]
  /** Set only on a reply shown at top level because its parent is hidden. */
  replyTo: T | null
}

export interface CommentTreeAccessors<T> {
  id: (item: T) => string | null | undefined
  parentId: (item: T) => string | null | undefined
  time: (item: T) => number
}

export function buildCommentTree<T>(
  visible: T[],
  { id, parentId, time }: CommentTreeAccessors<T>,
  all: T[] = visible
): CommentThread<T>[] {
  const byId = new Map<string, T>()
  for (const item of visible) {
    const key = id(item)
    if (key) byId.set(key, item)
  }
  const known = new Map<string, T>()
  for (const item of all) {
    const key = id(item)
    if (key) known.set(key, item)
  }

  const children = new Map<string, T[]>()
  const roots: T[] = []
  for (const item of visible) {
    const parent = parentId(item)
    if (parent && parent !== id(item) && byId.has(parent)) {
      const list = children.get(parent) ?? []
      list.push(item)
      children.set(parent, list)
    } else {
      roots.push(item)
    }
  }

  // A cycle in bad data must not loop forever or drop comments.
  const placed = new Set<T>()
  const build = (item: T, isRoot: boolean): CommentThread<T> => {
    placed.add(item)
    const key = id(item)
    const kids = (key ? children.get(key) ?? [] : [])
      .filter((k) => !placed.has(k))
      .sort((a, b) => time(a) - time(b))
    const parent = parentId(item)
    return {
      item,
      replies: kids.map((k) => build(k, false)),
      replyTo: isRoot && parent ? known.get(parent) ?? null : null,
    }
  }

  const threads = roots
    .sort((a, b) => time(b) - time(a))
    .map((r) => build(r, true))
  for (const item of visible) {
    if (!placed.has(item)) threads.push(build(item, true))
  }
  return threads
}
